import React, { useState, useEffect } from 'react';
import userService from '../services/userService';
import requestsService, { Request, UserRequestStats } from '../services/requestsService';
import { User } from '../services/userService';
import { ChevronRightIcon, UserIcon, PlusIcon, EditIcon, TrashIcon } from './icons';
import './MembersPage.css';

interface MembersPageProps {
  currentUser?: {
    id: number;
    email: string;
    username: string;
    name: string;
    surname: string;
    role: string;
  } | null;
}

const MembersPage: React.FC<MembersPageProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userRequests, setUserRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRequestsLoading, setUserRequestsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedAirport, setSelectedAirport] = useState<string>('');
  const [userStats, setUserStats] = useState<UserRequestStats | null>(null);
  const [requestType, setRequestType] = useState<'responsible' | 'all'>('responsible');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    name: '',
    surname: '',
    patronymic: '',
    role: 'USER',
    position: 'DRIVER',
    airport: '',
    enabled: true
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userService.getUsers();
      setUsers(response.content || response);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Ошибка при загрузке участников');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRequests = async (userId: number) => {
    try {
      setUserRequestsLoading(true);
      setError(null);
      
      let response: Request[];
      
      if (requestType === 'responsible') {
        // Получаем заявки, где пользователь является ответственным
        response = await requestsService.getRequestsByResponsible(userId);
      } else {
        // Получаем все заявки пользователя (как создателя, так и ответственного)
        response = await requestsService.getRequestsByUser(userId);
      }
      
      // API возвращает массив заявок напрямую, а не объект с content
      setUserRequests(Array.isArray(response) ? response : []);
      
      // Получаем статистику заявок пользователя
      try {
        const stats = await requestsService.getUserRequestStats(userId);
        setUserStats(stats);
      } catch (statsError) {
        console.error('Error fetching user stats:', statsError);
        // Не показываем ошибку статистики пользователю
      }
    } catch (err) {
      console.error('Error fetching user requests:', err);
      setError('Ошибка при загрузке заявок пользователя');
    } finally {
      setUserRequestsLoading(false);
    }
  };

  const handleUserClick = async (user: User) => {
    setSelectedUser(user);
    await fetchUserRequests(user.id);
  };

  const handleBackToUsers = () => {
    setSelectedUser(null);
    setUserRequests([]);
    setUserStats(null);
    setRequestType('responsible');
  };

  const handleCreateUserClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditUser = (user: User, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingUser(user);
    setFormData({
      email: user.email,
      username: user.username,
      password: '', // Пароль не показываем
      name: user.name,
      surname: user.surname,
      patronymic: user.patronymic || '',
      role: user.role,
      position: user.position || 'DRIVER',
      airport: user.airport || '',
      enabled: user.enabled
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = (user: User, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeletingUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    
    try {
      await userService.deleteUser(deletingUser.id);
      await fetchUsers();
      setIsDeleteModalOpen(false);
      setDeletingUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Ошибка при удалении пользователя');
    }
  };

  const handleCloseModals = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setEditingUser(null);
    setDeletingUser(null);
    setFormData({
      email: '',
      username: '',
      password: '',
      name: '',
      surname: '',
      patronymic: '',
      role: 'USER',
      position: 'DRIVER',
      airport: '',
      enabled: true
    });
    setFormErrors({});
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Очищаем ошибку для этого поля
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'Email обязателен';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Неверный формат email';
    }

    if (!formData.username) {
      errors.username = 'Имя пользователя обязательно';
    }

    if (!formData.name) {
      errors.name = 'Имя обязательно';
    }

    if (!formData.surname) {
      errors.surname = 'Фамилия обязательна';
    }

    if (!formData.position) {
      errors.position = 'Должность обязательна';
    }

    // Пароль обязателен только при создании
    if (isCreateModalOpen && !formData.password) {
      errors.password = 'Пароль обязателен';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUserSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      await userService.createUser(formData);
      await fetchUsers();
      handleCloseModals();
    } catch (error) {
      console.error('Error creating user:', error);
      setError('Ошибка при создании пользователя');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!validateForm() || !editingUser) return;

    try {
      setIsSubmitting(true);
      const updateData = { ...formData };
      // Не отправляем пароль, если он не изменен
      if (!updateData.password) {
        const { password, ...dataWithoutPassword } = updateData;
        await userService.updateUser(editingUser.id, dataWithoutPassword);
      } else {
        await userService.updateUser(editingUser.id, updateData);
      }
      await fetchUsers();
      handleCloseModals();
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Ошибка при обновлении пользователя');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Администратор';
      case 'MODERATOR':
        return 'Модератор';
      case 'USER':
        return 'Пользователь';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return '#dc2626';
      case 'MODERATOR':
        return '#f59e0b';
      case 'USER':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'HIGH':
        return '#ef4444';
      case 'MEDIUM':
        return '#f59e0b';
      case 'LOW':
        return '#10b981';
      case 'CRITICAL':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'HIGH':
        return 'Высокий';
      case 'MEDIUM':
        return 'Средний';
      case 'LOW':
        return 'Низкий';
      case 'CRITICAL':
        return 'Критический';
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.username.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = !selectedRole || user.role === selectedRole;
    const matchesAirport = !selectedAirport || user.airport === selectedAirport;
    
    return matchesSearch && matchesRole && matchesAirport;
  });

  const isAdmin = currentUser?.role === 'ADMIN';

  if (selectedUser) {
    return (
      <div className="members-page">
        <div className="members-header">
          <button onClick={handleBackToUsers} className="back-button">
            ← Назад к участникам
          </button>
          <h1>Заявки участника</h1>
        </div>

        <div className="selected-user-info">
          <div className="user-avatar">
            <UserIcon className="user-icon" size={24} />
          </div>
          <div className="user-details">
            <h2>{selectedUser.name} {selectedUser.surname}</h2>
            <p className="user-email">{selectedUser.email}</p>
            <div className="user-meta">
              <span 
                className="role-badge"
                style={{ backgroundColor: getRoleColor(selectedUser.role) }}
              >
                {getRoleText(selectedUser.role)}
              </span>
              {selectedUser.airport && (
                <span className="airport-badge">{selectedUser.airport}</span>
              )}
              <span className={`status-badge ${selectedUser.enabled ? 'active' : 'inactive'}`}>
                {selectedUser.enabled ? 'Активен' : 'Неактивен'}
              </span>
            </div>
          </div>
        </div>

        {userRequestsLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Загрузка заявок...</p>
          </div>
        ) : (
          <>
            {userStats && (
              <div className="user-stats">
                <h3>Статистика заявок</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Всего заявок:</span>
                    <span className="stat-value">{userStats.totalRequests}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Созданных:</span>
                    <span className="stat-value">{userStats.createdRequests}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Ответственный:</span>
                    <span className="stat-value">{userStats.responsibleRequests}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="requests-controls">
              <div className="request-type-toggle">
                <button
                  className={`toggle-btn ${requestType === 'responsible' ? 'active' : ''}`}
                  onClick={() => {
                    setRequestType('responsible');
                    if (selectedUser) {
                      fetchUserRequests(selectedUser.id);
                    }
                  }}
                >
                  Ответственный ({userStats?.responsibleRequests || 0})
                </button>
                <button
                  className={`toggle-btn ${requestType === 'all' ? 'active' : ''}`}
                  onClick={() => {
                    setRequestType('all');
                    if (selectedUser) {
                      fetchUserRequests(selectedUser.id);
                    }
                  }}
                >
                  Все заявки ({userStats?.totalRequests || 0})
                </button>
              </div>
            </div>

            {userRequests.length > 0 ? (
              <div className="user-requests">
                <h3>
                  {requestType === 'responsible' ? 'Заявки (ответственный)' : 'Все заявки'} 
                  ({userRequests.length})
                </h3>
                <div className="requests-list">
                  {userRequests.map((request) => (
                    <div key={request.id} className="request-card">
                      <div className="request-header">
                        <h4 className="request-title">{request.title}</h4>
                        <div className="request-badges">
                          <span 
                            className="priority-badge"
                            style={{ backgroundColor: getStatusColor(request.priority) }}
                          >
                            {getStatusText(request.priority)}
                          </span>
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: request.status.color }}
                          >
                            {request.status.name}
                          </span>
                        </div>
                      </div>
                      
                      <div className="request-details">
                        <div className="detail-row">
                          <span className="detail-label">Рейс:</span>
                          <span className="detail-value">
                            {request.flight ? `${request.flight.flightNumber} (${request.flight.departureAirport} → ${request.flight.arrivalAirport})` : 'Не указан'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Машина:</span>
                          <span className="detail-value">
                            {request.car ? `${request.car.name} ${request.car.model} (№${request.car.number})` : 'Не указана'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Части самолета:</span>
                          <span className="detail-value">
                            {request.aircraftParts && request.aircraftParts.length > 0 
                              ? request.aircraftParts.map(part => part.aircraftPartName).join(', ')
                              : 'Не указаны'
                            }
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Создано:</span>
                          <span className="detail-value">{formatDateTime(request.createdAt)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Создатель:</span>
                          <span className="detail-value">
                            {request.creator.name} {request.creator.surname}
                          </span>
                        </div>
                        {request.responsible && (
                          <div className="detail-row">
                            <span className="detail-label">Ответственный:</span>
                            <span className="detail-value">
                              {request.responsible.name} {request.responsible.surname}
                            </span>
                          </div>
                        )}
                        {requestType === 'all' && (
                          <div className="detail-row">
                            <span className="detail-label">Роль в заявке:</span>
                            <span className="detail-value">
                              {request.creator.id === selectedUser?.id ? (
                                <span className="role-indicator creator">Создатель</span>
                              ) : request.responsible?.id === selectedUser?.id ? (
                                <span className="role-indicator responsible">Ответственный</span>
                              ) : (
                                'Участник'
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p>У участника нет заявок</p>
              </div>
            )}
          </>
        )}

        {error && (
          <div className="error-container">
            <p>{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="members-page">
      <div className="members-header">
        <div className="header-content">
          <div>
            <h1>Участники</h1>
            <p>Управление участниками системы</p>
          </div>
          {isAdmin && (
                          <button onClick={handleCreateUserClick} className="btn create-user-btn">
                <PlusIcon size={20} />
                Добавить участника
              </button>
          )}
        </div>
      </div>

      <div className="members-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Поиск по имени, email или username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-container">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="filter-select"
          >
            <option value="">Все роли</option>
            <option value="ADMIN">Администратор</option>
            <option value="MODERATOR">Модератор</option>
            <option value="USER">Пользователь</option>
          </select>
          
          <select
            value={selectedAirport}
            onChange={(e) => setSelectedAirport(e.target.value)}
            className="filter-select"
          >
            <option value="">Все аэропорты</option>
            <option value="ALA">ALA</option>
            <option value="NQZ">NQZ</option>
            <option value="GUW">GUW</option>
            <option value="URA">URA</option>
            <option value="AKX">AKX</option>
            <option value="SCO">SCO</option>
            <option value="KGF">KGF</option>
            <option value="ASTANA">ASTANA</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка участников...</p>
        </div>
      ) : filteredUsers.length > 0 ? (
        <div className="users-grid">
          {filteredUsers.map((user) => (
            <div 
              key={user.id} 
              className="user-card"
              onClick={() => handleUserClick(user)}
            >
              <div className="user-avatar">
                <UserIcon className="user-icon" size={24} />
              </div>
              
              <div className="user-info">
                <h3 className="user-name">{user.name} {user.surname}</h3>
                <p className="user-email">{user.email}</p>
                <p className="user-username">@{user.username}</p>
                
                <div className="user-badges">
                  <span 
                    className="role-badge"
                    style={{ backgroundColor: getRoleColor(user.role) }}
                  >
                    {getRoleText(user.role)}
                  </span>
                  {user.airport && (
                    <span className="airport-badge">{user.airport}</span>
                  )}
                  <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                    {user.enabled ? 'Активен' : 'Неактивен'}
                  </span>
                </div>
                
                {user.position && (
                  <p className="user-position">{user.position}</p>
                )}
              </div>
              
              <div className="user-actions">
                {isAdmin && (
                  <>
                    <button
                      className="action-btn edit-btn"
                      onClick={(e) => handleEditUser(user, e)}
                      title="Редактировать"
                    >
                      <EditIcon size={16} />
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={(e) => handleDeleteUser(user, e)}
                      title="Удалить"
                    >
                      <TrashIcon size={16} />
                    </button>
                  </>
                )}
                <ChevronRightIcon className="chevron-icon" size={20} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>Участники не найдены</p>
        </div>
      )}

      {error && (
        <div className="error-container">
          <p>{error}</p>
        </div>
      )}

      {/* Модальное окно удаления пользователя */}
      {isDeleteModalOpen && deletingUser && (
        <div className="modal-overlay" onClick={handleCloseModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Удаление пользователя</h3>
              <button className="modal-close" onClick={handleCloseModals}>×</button>
            </div>
            <div className="modal-body">
              <p>Вы уверены, что хотите удалить пользователя <strong>{deletingUser.name} {deletingUser.surname}</strong>?</p>
              <p className="warning-text">Это действие нельзя отменить.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModals}>
                Отмена
              </button>
              <button className="btn btn-danger" onClick={handleConfirmDelete}>
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно создания пользователя */}
      {isCreateModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить участника</h3>
              <button className="modal-close" onClick={handleCloseModals}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={formErrors.email ? 'form-input error' : 'form-input'}
                    placeholder="user@example.com"
                  />
                  {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="username">Имя пользователя *</label>
                  <input
                    type="text"
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className={formErrors.username ? 'form-input error' : 'form-input'}
                    placeholder="username"
                  />
                  {formErrors.username && <span className="error-text">{formErrors.username}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Пароль *</label>
                  <input
                    type="password"
                    id="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={formErrors.password ? 'form-input error' : 'form-input'}
                    placeholder="Введите пароль"
                  />
                  {formErrors.password && <span className="error-text">{formErrors.password}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="name">Имя *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={formErrors.name ? 'form-input error' : 'form-input'}
                    placeholder="Иван"
                  />
                  {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="surname">Фамилия *</label>
                  <input
                    type="text"
                    id="surname"
                    value={formData.surname}
                    onChange={(e) => handleInputChange('surname', e.target.value)}
                    className={formErrors.surname ? 'form-input error' : 'form-input'}
                    placeholder="Иванов"
                  />
                  {formErrors.surname && <span className="error-text">{formErrors.surname}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="patronymic">Отчество</label>
                  <input
                    type="text"
                    id="patronymic"
                    value={formData.patronymic}
                    onChange={(e) => handleInputChange('patronymic', e.target.value)}
                    className="form-input"
                    placeholder="Иванович"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">Роль</label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="form-input"
                  >
                    <option value="USER">Пользователь</option>
                    <option value="MODERATOR">Модератор</option>
                    <option value="ADMIN">Администратор</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="position">Должность *</label>
                  <select
                    id="position"
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    className={formErrors.position ? 'form-input error' : 'form-input'}
                  >
                    <option value="DRIVER">Водитель</option>
                    <option value="MANAGER">Менеджер</option>
                    <option value="OPERATOR">Оператор</option>
                  </select>
                  {formErrors.position && <span className="error-text">{formErrors.position}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="airport">Аэропорт</label>
                  <select
                    id="airport"
                    value={formData.airport}
                    onChange={(e) => handleInputChange('airport', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Выберите аэропорт</option>
                    <option value="SVO">Шереметьево (SVO)</option>
                    <option value="DME">Домодедово (DME)</option>
                    <option value="VKO">Внуково (VKO)</option>
                    <option value="ALA">Алматы (ALA)</option>
                    <option value="NQZ">Нур-Султан (NQZ)</option>
                    <option value="GUW">Атырау (GUW)</option>
                    <option value="URA">Уральск (URA)</option>
                    <option value="AKX">Актобе (AKX)</option>
                    <option value="SCO">Актау (SCO)</option>
                    <option value="KGF">Караганда (KGF)</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => handleInputChange('enabled', e.target.checked)}
                      className="form-checkbox"
                    />
                    <span>Активен</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModals} disabled={isSubmitting}>
                Отмена
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCreateUserSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования пользователя */}
      {isEditModalOpen && editingUser && (
        <div className="modal-overlay" onClick={handleCloseModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Редактировать участника</h3>
              <button className="modal-close" onClick={handleCloseModals}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="edit-email">Email *</label>
                  <input
                    type="email"
                    id="edit-email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={formErrors.email ? 'form-input error' : 'form-input'}
                    placeholder="user@example.com"
                  />
                  {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-username">Имя пользователя *</label>
                  <input
                    type="text"
                    id="edit-username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className={formErrors.username ? 'form-input error' : 'form-input'}
                    placeholder="username"
                  />
                  {formErrors.username && <span className="error-text">{formErrors.username}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-password">Новый пароль</label>
                  <input
                    type="password"
                    id="edit-password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="form-input"
                    placeholder="Оставьте пустым, чтобы не изменять"
                  />
                  <small className="form-hint">Оставьте пустым, чтобы не изменять пароль</small>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-name">Имя *</label>
                  <input
                    type="text"
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={formErrors.name ? 'form-input error' : 'form-input'}
                    placeholder="Иван"
                  />
                  {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-surname">Фамилия *</label>
                  <input
                    type="text"
                    id="edit-surname"
                    value={formData.surname}
                    onChange={(e) => handleInputChange('surname', e.target.value)}
                    className={formErrors.surname ? 'form-input error' : 'form-input'}
                    placeholder="Иванов"
                  />
                  {formErrors.surname && <span className="error-text">{formErrors.surname}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-patronymic">Отчество</label>
                  <input
                    type="text"
                    id="edit-patronymic"
                    value={formData.patronymic}
                    onChange={(e) => handleInputChange('patronymic', e.target.value)}
                    className="form-input"
                    placeholder="Иванович"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-role">Роль</label>
                  <select
                    id="edit-role"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="form-input"
                  >
                    <option value="USER">Пользователь</option>
                    <option value="MODERATOR">Модератор</option>
                    <option value="ADMIN">Администратор</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-position">Должность *</label>
                  <select
                    id="edit-position"
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    className={formErrors.position ? 'form-input error' : 'form-input'}
                  >
                    <option value="DRIVER">Водитель</option>
                    <option value="MANAGER">Менеджер</option>
                    <option value="OPERATOR">Оператор</option>
                  </select>
                  {formErrors.position && <span className="error-text">{formErrors.position}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-airport">Аэропорт</label>
                  <select
                    id="edit-airport"
                    value={formData.airport}
                    onChange={(e) => handleInputChange('airport', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Выберите аэропорт</option>
                    <option value="SVO">Шереметьево (SVO)</option>
                    <option value="DME">Домодедово (DME)</option>
                    <option value="VKO">Внуково (VKO)</option>
                    <option value="ALA">Алматы (ALA)</option>
                    <option value="NQZ">Нур-Султан (NQZ)</option>
                    <option value="GUW">Атырау (GUW)</option>
                    <option value="URA">Уральск (URA)</option>
                    <option value="AKX">Актобе (AKX)</option>
                    <option value="SCO">Актау (SCO)</option>
                    <option value="KGF">Караганда (KGF)</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => handleInputChange('enabled', e.target.checked)}
                      className="form-checkbox"
                    />
                    <span>Активен</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModals} disabled={isSubmitting}>
                Отмена
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleUpdateUser}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersPage;
